import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Import, Paperclip2 } from "iconsax-react";
import { CloseIcon } from "@/shared/assets/icons";
import { Button } from "@/shared/ui";

interface FileUploadProps {
  label?: string;
  hasRequiredLabel?: boolean;
  maxSizeMB?: number;
  accept?: string;
  value?: File | null;
  existingFileUrl?: string | null;
  /** Explicit name for the existing file — the URL is not always a filename. */
  existingFileName?: string;
  onChange?: (file: File | null) => void;
  onRemoveExisting?: () => void;
  onValidationError?: (message: string | null) => void;
  multiple?: boolean;
  onFilesSelect?: (files: File[]) => void;
  error?: string | null;
  /** @deprecated FileUploader reads its own strings from the Common namespace; this prop is ignored. */
  namespace?: string;
  formatsText?: string;
}

export default function FileUploader({
  label,
  hasRequiredLabel = false,
  maxSizeMB = 5,
  accept = ".pdf,.jpg,.jpeg,.png",
  value,
  existingFileUrl,
  existingFileName,
  onChange,
  onRemoveExisting,
  onValidationError,
  multiple = false,
  onFilesSelect,
  error,
  formatsText,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  // FileUploader's own strings live in the always-loaded Common namespace, so a
  // lazy per-feature namespace can never suspend (and blank out) the host page.
  const { t } = useTranslation("Common");

  const derivedExistingFileName = useMemo(() => {
    if (!existingFileUrl) return null;
    if (existingFileName) return existingFileName;
    const fromUrl = existingFileUrl.split("/").filter(Boolean).pop()?.split("?")[0];
    return decodeURIComponent(fromUrl || "document");
  }, [existingFileUrl, existingFileName]);

  function rejectFile(message: string) {
    setValidationError(message);
    onValidationError?.(message);
  }

  function getFileError(file: File) {
    if (file.size > maxSizeMB * 1024 * 1024) {
      return t("file_uploader.errors.file_too_large", { max: maxSizeMB });
    }
    const allowedExtensions = accept.split(",").map((ext) => ext.trim().toLowerCase());
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      return t("file_uploader.errors.invalid_format");
    }
    return null;
  }

  function processFiles(fileList: FileList | null | undefined) {
    if (!multiple) {
      processFile(fileList?.[0] ?? null);
      return;
    }
    const files = Array.from(fileList ?? []);
    const rejected = files.map((file) => ({ file, error: getFileError(file) })).find((item) => item.error);
    const message = rejected ? `${rejected.file.name}: ${rejected.error}` : null;
    setValidationError(message);
    onValidationError?.(message);
    const accepted = files.filter((file) => !getFileError(file));
    if (accepted.length) onFilesSelect?.(accepted);
  }

  function processFile(file: File | null) {
    if (!file) {
      setValidationError(null);
      onValidationError?.(null);
      onChange?.(null);
      return;
    }
    const fileError = getFileError(file);
    if (fileError) {
      rejectFile(fileError);
      return;
    }

    setValidationError(null);
    onValidationError?.(null);
    onChange?.(file);
  }

  function handleSelect(e: React.ChangeEvent<HTMLInputElement>) {
    processFiles(e.target.files);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);

    processFiles(e.dataTransfer.files);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      inputRef.current?.click();
    }
  }

  function humanSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  function handleRemove() {
    if (value) onChange?.(null);
    if (existingFileUrl) onRemoveExisting?.();
    setValidationError(null);
    onValidationError?.(null);
  }

  const showFileCard = !multiple && (Boolean(value) || Boolean(existingFileUrl));
  const fileUrl = value ? URL.createObjectURL(value) : existingFileUrl!;
  const displayError = error || validationError;

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <p className="text-body-bold-sm content-base-primary">
          {label}
          {hasRequiredLabel && <span className="text-label-sm content-action-negative ml-1">*</span>}
        </p>
      )}

      {showFileCard && (
        <div className="surface-tertiary-fill rounded-[10px] p-0.5 input-box-shadow flex items-center">
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-1 items-center gap-2 radius-xs surface-base-fill py-3 px-2 cursor-pointer hover:opacity-90 transition-opacity duration-150 min-w-0">
            <span className="content-base-low shrink-0">
              <Paperclip2 size={16} color="currentColor" />
            </span>
            <p className="content-base-primary text-label-sm truncate min-w-0 flex-1">
              {value?.name ?? derivedExistingFileName}
            </p>
            {value && <span className="text-label-sm content-action-neutral shrink-0">{humanSize(value.size)}</span>}
          </a>

          <button
            type="button"
            onClick={handleRemove}
            className="w-10 aspect-square flex items-center justify-center content-action-neutral hover:opacity-80 transition-opacity duration-150 shrink-0">
            <CloseIcon />
          </button>
        </div>
      )}

      {!showFileCard && (
        <div
          tabIndex={0}
          role="button"
          className={cn(
            "radius-md border border-dashed surface-component-stroke surface-base-fill flex flex-col items-center justify-center gap-3 py-10 px-7 cursor-pointer w-full",
            "input-box-shadow outline-none focus-visible:ring-2 focus-visible:ring-brand-200",
            isDragging && "surface-secondary-fill!",
            displayError && "border-action-negative",
          )}
          onClick={() => inputRef.current?.click()}
          onKeyDown={handleKeyDown}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}>
          <div className="flex flex-col items-center justify-center gap-5">
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 flex justify-center items-center radius-sm background-neutral-fill radial-glow elevation-level-1">
                <span className="text-white">
                  <Import size={24} variant="Bold" color="currentColor" />
                </span>
              </div>
              <p className="text-body-bold-md content-base-primary text-center max-w-md">
                {t("file_uploader.drop_here")}
              </p>
              <p className="text-body-regular-sm content-base-low text-center max-w-md">
                {formatsText || t("file_uploader.formats", { max: maxSizeMB })}
              </p>
            </div>
            <Button variant="secondary" size="sm">
              {t("file_uploader.choose_file")}
            </Button>
          </div>

          <input type="file" ref={inputRef} className="hidden" accept={accept} multiple={multiple} onChange={handleSelect} />
        </div>
      )}

      {displayError && <p className="text-body-regular-xs content-action-negative">{displayError}</p>}
    </div>
  );
}
